import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const CHAT_WIDGET_SCRIPT_ID = "md-chat-widget-script";
export const CHAT_WIDGET_URL = "https://md-mu-lemon.vercel.app/embed.js";
export const CHAT_WIDGET_API_URL = "https://md-bot-ntku.onrender.com";

const CHAT_WIDGET_SELECTORS = [
  '[id*="md-chat-widget"]',
  '[class*="md-chat-widget"]',
  "[data-md-chat-widget]",
  `iframe[src*="${CHAT_WIDGET_API_URL}"]`,
  'iframe[src*="md-mu-lemon.vercel.app"]',
];

const getChatWidgetNodes = () =>
  CHAT_WIDGET_SELECTORS.flatMap((selector) =>
    Array.from(document.querySelectorAll(selector))
  ).filter((node) => node.id !== CHAT_WIDGET_SCRIPT_ID);

const setChatWidgetVisibility = (isVisible) => {
  getChatWidgetNodes().forEach((node) => {
    node.style.display = isVisible ? "" : "none";
    node.setAttribute("aria-hidden", isVisible ? "false" : "true");
  });
};

const removeChatWidgetScript = () => {
  document.getElementById(CHAT_WIDGET_SCRIPT_ID)?.remove();
};

const appendChatWidgetScript = () => {
  const script = document.createElement("script");
  script.id = CHAT_WIDGET_SCRIPT_ID;
  script.src = CHAT_WIDGET_URL;
  script.async = true;
  script.setAttribute("data-api-url", CHAT_WIDGET_API_URL);
  script.setAttribute("data-position", "right");
  document.body.appendChild(script);
  return script;
};

const ensureChatWidgetScript = () => {
  const existingScript = document.getElementById(CHAT_WIDGET_SCRIPT_ID);
  if (existingScript) return existingScript;
  return appendChatWidgetScript();
};

const reloadChatWidgetScript = () => {
  removeChatWidgetScript();
  return appendChatWidgetScript();
};

const MathDingoChatWidget = () => {
  const location = useLocation();
  const shouldShowChatWidget = location.pathname === "/yearsdashboard";

  useEffect(() => {
    setChatWidgetVisibility(shouldShowChatWidget);

    if (typeof MutationObserver === "undefined") return undefined;

    const observer = new MutationObserver(() => {
      setChatWidgetVisibility(shouldShowChatWidget);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [shouldShowChatWidget]);

  useEffect(() => {
    if (!shouldShowChatWidget) {
      setChatWidgetVisibility(false);
      return undefined;
    }

    setChatWidgetVisibility(true);
    ensureChatWidgetScript();

    const retryDelays = [1200, 3000];
    const retryIds = retryDelays.map((delay) =>
      window.setTimeout(() => {
        const hasWidgetNode = getChatWidgetNodes().length > 0;
        if (!hasWidgetNode) {
          reloadChatWidgetScript();
        }
        setChatWidgetVisibility(true);
      }, delay)
    );

    return () => {
      retryIds.forEach((retryId) => window.clearTimeout(retryId));
    };
  }, [shouldShowChatWidget]);

  return null;
};

export default MathDingoChatWidget;
